import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BrandsPageContext } from "@insite/content-library/Pages/BrandsPage";
import { HomePageContext } from "@insite/content-library/Pages/HomePage";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
    maximumBrandImageHeight = "maximumBrandImageHeight",
    showMoreLinkUrl = "showMoreLinkUrl",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumNumberOfBrandsPerRow]: string;
        [fields.maximumBrandImageHeight]: number;
        [fields.showMoreLinkUrl]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        randomize: props.fields.brandsToDisplay === "random",
        pageSize: parseInt(props.fields.numberOfBrandsToDisplay || "0", 10),
        select: "id,detailPagePath,logoSmallImagePath,logoAltText",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;
    let brandPageUrl = getPageLinkByPageType(state, "BrandsPage")?.url;

    if (brandPageUrl === "" || brandPageUrl === undefined) {
        brandPageUrl = props.fields.showMoreLinkUrl;
    }

    const location = getLocation(state);
    const isBrandPage = location.pathname === brandPageUrl;
    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
        brandPageUrl,
        isBrandPage,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandListStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
    seeMoreLink?: LinkPresentationProps;
}

export const listStyles: BrandListStyles = {
    titleText: {
        variant: "h2",
        transform: "inherit",
        css: css`
            border-width: 1.25rem
            text-align: left;
            font-size: 1.25rem;
            border-bottom: 1px solid ${themeColors.colorBorderBrands};
            padding-bottom: .625rem;
            margin-bottom: .625rem;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            border-bottom: none;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};
        `,
    },
    imageItem: {
        css: css`
            padding: .31250625rem;
            justify-content: center;
            border-right: 1px solid ${themeColors.colorBorderBrands};
            border-bottom: 1px solid ${themeColors.colorBorderBrands};

            &:nth-child(4n) {
                border-right: 1px solid ${themeColors.colorBorderBrands};
            }  

            &:first-child {
                border-left:  1px solid ${themeColors.colorBorderBrands};
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-basis: 50%;
                            max-width: 50%;
                            &:nth-child(4n) {
                                border-right: 1px solid ${themeColors.colorBorderBrands};
                            }

                            &:nth-last-child(-n + 4) {
                                border-bottom: 1px solid ${themeColors.colorBorderBrands};
                            }

                            &:nth-child(2n) {
                                border-right: none;
                            }

                            &:nth-last-child(-n + 2) {
                                border-bottom: none;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )};
        `,
    },

    container: {
        css: css`
            margin: 0 1.25rem;
            background: ${themeColors.colorWhite};
            padding: 2.5rem 3.75rem 2.5rem 2.5rem;
            position: relative;
            box-shadow: 0 10px 20px 0 rgba(46,91,255,.07);

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: .9375rem;
                            margin: 0;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )};
        `,
    },
    seeMoreLink: {
        css: css`
            cursor: pointer;
            font-size: ${themeFontSizes.size14};
            font-weight: ${themeFontWeights.fontWeightBold};
            line-height: 1.0625rem;
            padding-left: 1.25rem;
            border-left: 1px solid ${themeColors.colorBorderBrands};
            position: absolute;
            right: 3.75rem;
            top: 2.5rem;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            right: .938rem;
                            top: 1.375rem;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )};
            span {
                color: ${themeColors.colorBrandsSeeMore} !important;

                &:hover{
                    color: ${themeColors.colorButtonHover} !important;
                }
            }
        `,
    },
};

const styles = listStyles;

class BrandGallery extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandGallery, shouldLoadBrandGallery, brandGalleryParameter } = this.props;
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }
    render() {
        const {
            brandsState: { isLoading, value: brandList },
            fields,
            brandPageUrl,
            isBrandPage,
        } = this.props;

        if (isLoading || !brandList) {
            return null;
        }

        const desktop = parseInt(fields.maximumNumberOfBrandsPerRow, 10);
        const totalPageSize = parseInt(fields.numberOfBrandsToDisplay, 10);
        const tablet = Math.ceil(desktop / 2);
        const mobile = Math.ceil(tablet / 2);

        let noBottomStyle: number;
        if (totalPageSize > desktop) {
            noBottomStyle = Math.ceil(totalPageSize % desktop);
            if (noBottomStyle === 0) {
                noBottomStyle = desktop;
            }
        } else {
            noBottomStyle = Math.abs(desktop);
        }

        const imgProps = {
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: `${fields.maximumBrandImageHeight}px`,
            },
        };

        styles.imageItem = {
            css: css`
                padding: .31250625rem;
                justify-content: center;
                border-right: 1px solid ${themeColors.colorBorderBrands};
                border-bottom: 1px solid ${themeColors.colorBorderBrands};
    
                &:nth-child(4n) {
                    border-right: 1px solid ${themeColors.colorBorderBrands};
                }

                &:first-child {
                    border-left:  none;
                }
    
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                flex-basis: 50%;
                                max-width: 50%;
                                &:nth-child(4n) {
                                    border-right: 1px solid ${themeColors.colorBorderBrands};
                                }
    
                                &:nth-last-child(-n + 4) {
                                    border-bottom: 1px solid ${themeColors.colorBorderBrands};
                                }
    
                                &:nth-child(2n) {
                                    border-right: none;
                                }
    
                                &:nth-last-child(-n + 2) {
                                    border-bottom: none;
                                }
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )
                };
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                &:nth-child(${desktop}n) {
                                    border-right: none;
                                }

                                &:nth-last-child(-n + ${noBottomStyle} ) {
                                    border-bottom: none;
                                }

                                &:nth-child(-n + ${desktop}) {
                                    border-bottom: 1px solid ${themeColors.colorBorderBrands};                          
                                }

                                &:last-child {
                                    border-right:  none;
                                }

                                &:nth-child(${desktop}n + 1) {
                                    border-left: none;
                                }
                                
                            `,
                            null,
                        ],
                        "min",
                    )
                };
            `,
        };

        return (
            <StyledWrapper {...styles.container} data-test-selector="brandGallery">
                {this.props.fields.title && (
                    <Typography variant="headerSecondary" {...styles.titleText} data-test-selector="brandGalleryTitle">
                        {this.props.fields.title}
                    </Typography>
                )}
                {!isBrandPage && (
                    <Link href={brandPageUrl} {...styles.seeMoreLink} className="brand-see-more">
                        {translate("Show More")}
                    </Link>
                )}
                <FlexWrapContainer {...styles.imageContainer}>
                    {brandList.map(brand => {
                        if (brand === brandList[0]) {
                            const focusFunction = () => {
                                const elementToFocus = document.getElementById("collapseButton");
                                if (elementToFocus) {
                                    elementToFocus.focus();
                                } else {
                                    document.getElementById("expandButton")?.focus();
                                }
                            };
                            return (
                                <FlexItem
                                    key={brand.id}
                                    flexColumns={[mobile, tablet, tablet, desktop, desktop]}
                                    {...styles.imageItem}
                                >
                                    <SkipNav
                                        extendedStyles={styles.skipToContent}
                                        text={translate("Skip to full brand list")}
                                        focusFunction={focusFunction}
                                    />
                                    <Clickable href={`${brand.detailPagePath  }/category/all`} {...styles.link}>
                                        <LazyImage
                                            imgProps={imgProps}
                                            {...styles.image}
                                            src={brand.logoSmallImagePath}
                                            altText={
                                                brand.logoAltText
                                                    ? `${translate("Go to brand page")} ${brand.logoAltText}`
                                                    : `${translate("Go to brand page")} ${brand.name}`
                                            }
                                        />
                                    </Clickable>
                                </FlexItem>
                            );
                        }
                        return (
                            <FlexItem
                                key={brand.id}
                                flexColumns={[mobile, tablet, tablet, desktop, desktop]}
                                {...styles.imageItem}
                            >
                                <Clickable href={`${brand.detailPagePath  }/category/all`} {...styles.link}>
                                    <LazyImage
                                        imgProps={imgProps}
                                        {...styles.image}
                                        src={brand.logoSmallImagePath}
                                        altText={
                                            brand.logoAltText
                                                ? `${translate("Go to brand page")} ${brand.logoAltText}`
                                                : `${translate("Go to brand page")} ${brand.name}`
                                        }
                                    />
                                </Clickable>
                            </FlexItem>
                        );
                    })}
                </FlexWrapContainer>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandGallery),
    definition: {
        group: "Brands",
        icon: "Image",
        displayName: "Brand Gallery",
        allowedContexts: [BrandsPageContext, HomePageContext],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Random",
                        value: "random",
                    },
                    {
                        displayName: "Select brands",
                        value: "select-brands",
                    },
                ],
                defaultValue: "random",
                fieldType: "General",
                tooltip:
                    "Brand logos can be picked at random on page load, or you can select specific brands to display.",
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "DropDownField",
                defaultValue: "12",
                options: [
                    { displayName: "4", value: "4" },
                    { displayName: "8", value: "8" },
                    { displayName: "12", value: "12" },
                    { displayName: "16", value: "16" },
                    { displayName: "20", value: "20" },
                    { displayName: "24", value: "24" },
                ],
                fieldType: "General",
                isVisible: item => item.fields.brandsToDisplay === "random",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
                defaultValue: [],
                fieldType: "General",
            },
            {
                name: fields.maximumNumberOfBrandsPerRow,
                displayName: "Maximum brands per row",
                editorTemplate: "DropDownField",
                defaultValue: "4",
                options: [
                    { displayName: "4", value: "4" },
                    { displayName: "8", value: "8" },


                ],
                fieldType: "General",
                tooltip:
                    "Up to 12 brands can display per row. ",
            },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "General",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
            {
                name: fields.showMoreLinkUrl,
                displayName: "Show More Link Url",
                editorTemplate: "TextField",
                defaultValue: "/brand",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
